import { type FC, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import type { User } from "../stores/user_store";
import { navEntries, isSeparator } from "./sidebar_nav";

export const Sidebar: FC<{ user: User }> = ({ user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <aside className="vmx-sidebar">
      {/* Logo / Brand */}
      <div
        className="vmx-brand"
        style={{
          height: "var(--vmx-header-height)",
          display: "flex",
          alignItems: "center",
          padding: "0 24px",
          gap: 12,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <img src="/favicon.svg" alt="SecureCycle" width={36} height={35} />
        <div className="vmx-brand-text">
          <div
            style={{
              fontSize: "1rem",
              fontWeight: 700,
              color: "#fff",
              lineHeight: 1.2,
              letterSpacing: "-0.3px",
            }}
          >
            SecureCycle
          </div>
          <div
            style={{
              fontSize: "0.6875rem",
              color: "var(--vmx-sidebar-text)",
              opacity: 0.6,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
            }}
          >
            Verimatrix
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav
        style={{
          flex: 1,
          padding: "16px 12px",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {navEntries.map((entry, idx) => {
          if (isSeparator(entry)) {
            return (
              <div
                key={`sep-${idx}`}
                style={{
                  height: 1,
                  background: "rgba(255,255,255,0.08)",
                  margin: "8px 16px",
                }}
              />
            );
          }
          const item = entry;
          const isActive =
            item.path === "/"
              ? location.pathname === "/"
              : location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 16px",
                borderRadius: "var(--vmx-radius)",
                fontSize: "0.875rem",
                fontWeight: isActive ? 600 : 500,
                color: isActive
                  ? "var(--vmx-sidebar-active-text)"
                  : "var(--vmx-sidebar-text)",
                background: isActive
                  ? "var(--vmx-sidebar-active)"
                  : "transparent",
                textDecoration: "none",
                transition: "all var(--vmx-transition)",
              }}
              onMouseEnter={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "rgba(255,255,255,0.05)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              <span
                style={{ fontSize: "1.125rem", width: 24, textAlign: "center" }}
                title={item.label}
              >
                {item.icon}
              </span>
              <span className="vmx-nav-label">{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* User / Sign out */}
      <div
        style={{
          padding: "12px",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          position: "relative",
        }}
      >
        <button
          className="vmx-user-btn"
          onClick={() => setMenuOpen(!menuOpen)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "10px 12px",
            borderRadius: "var(--vmx-radius)",
            border: "none",
            background: menuOpen ? "rgba(255,255,255,0.08)" : "transparent",
            cursor: "pointer",
            transition: "background var(--vmx-transition)",
          }}
          onMouseEnter={(e) => {
            if (!menuOpen)
              e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }}
          onMouseLeave={(e) => {
            if (!menuOpen) e.currentTarget.style.background = "transparent";
          }}
        >
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: "50%",
              background:
                "linear-gradient(135deg, var(--vmx-accent-dark), var(--vmx-accent))",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.75rem",
              flexShrink: 0,
            }}
          >
            {user.name
              .split(" ")
              .map((n) => n[0])
              .join("")
              .slice(0, 2)
              .toUpperCase()}
          </div>
          <div
            className="vmx-user-info"
            style={{ flex: 1, minWidth: 0, textAlign: "left" }}
          >
            <div
              style={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                color: "rgba(255,255,255,0.9)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.name}
            </div>
            <div
              style={{
                fontFamily: "var(--vmx-font)",
                fontSize: "0.6875rem",
                color: "rgba(255,255,255,0.4)",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {user.email}
            </div>
          </div>
          <span
            className="vmx-user-arrow"
            style={{
              fontSize: "0.6875rem",
              color: "rgba(255,255,255,0.4)",
              flexShrink: 0,
            }}
          >
            ▲
          </span>
        </button>

        {menuOpen && (
          <>
            <div
              style={{ position: "fixed", inset: 0, zIndex: 99 }}
              onClick={() => setMenuOpen(false)}
            />
            <div
              className="vmx-user-menu"
              style={{
                position: "absolute",
                bottom: "calc(100% + 4px)",
                left: 12,
                right: 12,
                background: "var(--vmx-surface)",
                borderRadius: "var(--vmx-radius)",
                boxShadow: "var(--vmx-shadow-lg)",
                border: "var(--vmx-border)",
                zIndex: 100,
                padding: "6px",
                animation: "vmx-fadeIn 100ms ease",
              }}
            >
              <div
                style={{
                  fontSize: "0.625rem",
                  color: "var(--vmx-muted)",
                  textAlign: "center",
                  padding: "4px 6px 2px",
                  letterSpacing: "0.5px",
                }}
              >
                {__BUILD_VERSION__}
              </div>
              <div
                style={{
                  height: 1,
                  background: "var(--vmx-border-color, rgba(0,0,0,0.08))",
                  margin: "4px 0",
                }}
              />
              <button
                className="vmx-btn vmx-btn-ghost"
                style={{
                  width: "100%",
                  justifyContent: "flex-start",
                  color: "var(--vmx-danger)",
                  borderRadius: "var(--vmx-radius-sm)",
                }}
                onClick={() => {
                  setMenuOpen(false);
                  navigate("/logout");
                }}
              >
                ↪ Sign out
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
